import React, { useState, useEffect, useRef } from 'react';
import { useTheme, useMediaQuery } from '@mui/material';
import { Box, Container, IconButton, Tooltip, Collapse, Button, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Typography, FormControl, InputLabel, Select, MenuItem, Switch } from '@mui/material';
import MicIcon from '@mui/icons-material/Mic';
import StopIcon from '@mui/icons-material/Stop';
import SendIcon from '@mui/icons-material/Send';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import MenuIcon from '@mui/icons-material/Menu';
import TimerIcon from '@mui/icons-material/Timer';
import RefreshIcon from '@mui/icons-material/Refresh';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ThumbDownAltIcon from '@mui/icons-material/ThumbDownAlt'; // Import ThumbDownAltIcon
import ThumbDownAltOutlinedIcon from '@mui/icons-material/ThumbDownAltOutlined'; // Re-import outlined icon
import axios from 'axios';
import CircularProgress from '@mui/material/CircularProgress';
import { keyframes } from '@emotion/react';
import { API_URL, getUiLanguage, getSpeechLanguage } from '../config';
import usePracticeLocalization, { ENGLISH_CONTENT_FALLBACKS } from '../hooks/usePracticeLocalization';
import { FFmpeg } from '@ffmpeg/ffmpeg';
import { fetchFile, toBlobURL } from '@ffmpeg/util';

import TopSection from './TopSection';
import ChatMessages from './ChatMessages';
import ChatInput from './ChatInput';
import ModelSelector from './ModelSelector';
import scenarios from '../data/scenarios.json';
import DifficultySelector from './DifficultySelector';
import ResponseLengthSelector from './ResponseLengthSelector';
import AnalysisDialog from './AnalysisDialog';
import FlowchartView from './FlowchartView.js';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import FeedbackComponent from './FeedbackComponent';
import { useLanguage } from '../contexts/LanguageContext';

const highlightAnimation = keyframes`
  0% { background-color: rgba(255, 193, 7, 0.5); }
  50% { background-color: rgba(255, 193, 7, 0.8); }
  100% { background-color: rgba(25, 118, 210, 0.1); }
`;
const ChatBox = () => {
  const { tr, language, isEnglish } = useLanguage();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [models, setModels] = useState([]);
  const [selectedModel, setSelectedModel] = useState('');
  const [selectedScenario, setSelectedScenario] = useState(() => {
    const pathParts = window.location.pathname.split('/');
    const scenarioFromUrl = pathParts[2]; // Gets 'caseX' from '/chat/caseX'
    return scenarioFromUrl || 'case1'; // Fallback to 'case1' if no scenario in URL
  });
  const [isRecording, setIsRecording] = useState(false);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const [difficulty, setDifficulty] = useState('normal');
  const [responseLength, setResponseLength] = useState(50);
  const [debugInfo, setDebugInfo] = useState(null);
  const [isDebugOpen, setIsDebugOpen] = useState(false);
  const [isOptionsOpen, setIsOptionsOpen] = useState(false);
  const [showNameDialog, setShowNameDialog] = useState(false);
  const [isScenarioOpen, setIsScenarioOpen] = useState(true);
  const [showDebug] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('debug') === '1';
  });
  const [isProcessingSpeech, setIsProcessingSpeech] = useState(false);
  const [isChatComplete, setIsChatComplete] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [analysisOpen, setAnalysisOpen] = useState(false);
  const [isAnalysisLoading, setIsAnalysisLoading] = useState(false);
  const [currentStage, setCurrentStage] = useState('破冰階段');
  const [regeneratingMessageId, setRegeneratingMessageId] = useState(null);
  const [editingMessageId, setEditingMessageId] = useState(null);
  const [nodes, setNodes] = useState([]);
  const [isFlowchartLoading, setIsFlowchartLoading] = useState(false);
  const [elapsedTime, setElapsedTime] = useState(0); // Stopwatch starts at 0
  const [timeLimit, setTimeLimit] = useState(3 * 60); // Default time limit: 3 minutes
  const [isTimeLimitReached, setIsTimeLimitReached] = useState(false);
  const [isTimerActive, setIsTimerActive] = useState(true);
  const timerRef = useRef(null);
  const [currentScenario, setCurrentScenario] = useState(null);
  const [activeNode, setActiveNode] = useState('start');
  const [scenarioData, setScenarioData] = useState(null);
  const [showAdvisorMessage, setShowAdvisorMessage] = useState(true); // Default is on
  const [advisorMessage, setAdvisorMessage] = useState('');
  const [isLoadingAdvisorMessage, setIsLoadingAdvisorMessage] = useState(false);
  const [ffmpeg, setFfmpeg] = useState(null);
  const [ffmpegLoaded, setFfmpegLoaded] = useState(false);
  const [checkpointStatus, setCheckpointStatus] = useState({});
  const [error, setError] = useState(null);
  const [analysisCountdown, setAnalysisCountdown] = useState(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [showAnalysis, setShowAnalysis] = useState(false);
  const [analysisData, setAnalysisData] = useState(null);
  const [currentConversationId, setCurrentConversationId] = useState(null);
  const [isAdvisorMessageDisliked, setIsAdvisorMessageDisliked] = useState(false); // Re-add state
  // English display copy for the selected scenario (shared with the flowchart and assessment report)
  const { translateContent, isLocalizationLoading } = usePracticeLocalization(selectedScenario);

  // State for direct voice input sending
  const [sendVoiceDirectly, setSendVoiceDirectly] = useState(() => {
    const saved = localStorage.getItem('sendVoiceDirectlySetting');
    // Default to true unless explicitly set to false
    return saved !== 'false';
  });

  // Add a ref to track initial load
  const isInitialLoad = useRef(true);

  // Add these state variables
  const [dbConnectionStatus, setDbConnectionStatus] = useState(null);
  // Add a ref to track when to skip advisor fetch
  const skipNextAdvisorFetch = useRef(false);

  const chatContainerRef = useRef(null); // Add this ref for the feedback component

  useEffect(() => {
    setIsScenarioOpen(!isMobile);
  }, [isMobile]);

  // Effect to persist sendVoiceDirectly setting
  useEffect(() => {
    localStorage.setItem('sendVoiceDirectlySetting', sendVoiceDirectly);
  }, [sendVoiceDirectly]);

  useEffect(() => {
    const fetchModels = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/models`);
        setModels(response.data.models);
        if (response.data.models.length > 0) {
          setSelectedModel(response.data.models[0]);
        }
      } catch (error) {
        console.error('Error fetching models:', error);
      }
    };
    fetchModels();
  }, []);

  useEffect(() => {
    const advisorName = localStorage.getItem('advisorName');
    if (!advisorName || advisorName.trim() === '') {
      setShowNameDialog(true);
    }
  }, []);

  useEffect(() => {
    const currentScenario = scenarios.find(s => s.id === selectedScenario);
    const rawGreetingText = currentScenario?.greeting || '';
    const greetingText = isEnglish
      ? (ENGLISH_CONTENT_FALLBACKS[rawGreetingText] || '(Phone ringing… the customer answers.)')
      : rawGreetingText;

    // Generate and set a new conversation ID when the scenario changes
    const newConversationId = Date.now();
    setCurrentConversationId(newConversationId);
    console.log(`New conversation started with ID: ${newConversationId}`);

    // Set scenario data
    setScenarioData(currentScenario);

    // Set initial stage when scenario changes
    if (currentScenario?.stage && currentScenario.stage.length > 0) {
      setCurrentStage(currentScenario.stage[0].name);
    } else {
      setCurrentStage('破冰階段');
    }

    // Reset active node to 'start' when scenario changes
    setActiveNode('start');

    // Automatically add the greeting message when scenario changes
    if (greetingText) {
      const greetingMessage = {
        id: Date.now(),
        text: greetingText,
        sender: 'bot',
        timestamp: new Date().toISOString()
      };
      setMessages([greetingMessage]); // Reset messages and add greeting as first message
    } else {
      setMessages([]); // Just reset messages if no greeting text
    }
  }, [selectedScenario, language, isEnglish]);

  // Initialize timer when component mounts
  useEffect(() => {
    if (isTimerActive) {
      timerRef.current = setInterval(() => {
        setElapsedTime(prev => {
          const newTime = prev + 1;
          // Check if time limit is reached
          if (newTime >= timeLimit && !isTimeLimitReached) {
            setIsTimeLimitReached(true);
            // setIsChatComplete(true);
          }
          return newTime;
        });
      }, 1000);
    }

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [isTimerActive, timeLimit, isTimeLimitReached]);

  // Format time remaining as MM:SS
  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const handleScenarioChange = (event) => {
    setSelectedScenario(event.target.value);
  };

  const handleModelChange = (event) => {
    setSelectedModel(event.target.value);
  };

  const handleDifficultyChange = (event) => {
    setDifficulty(event.target.value);
  };

  const handleStageChange = (event) => {
    setCurrentStage(event.target.value);
  };

  // Initialize ffmpeg on component mount
  useEffect(() => {
    const loadFFmpeg = async () => {
      try {
        // Create FFmpeg instance
        const ffmpegInstance = new FFmpeg();

        // Use local packages instead of CDN
        await ffmpegInstance.load({
          coreURL: await toBlobURL(
            new URL('/node_modules/@ffmpeg/core/dist/umd/ffmpeg-core.js', import.meta.url).href,
            'text/javascript'
          ),
          wasmURL: await toBlobURL(
            new URL('/node_modules/@ffmpeg/core/dist/umd/ffmpeg-core.wasm', import.meta.url).href,
            'application/wasm'
          )
        });

        setFfmpeg(ffmpegInstance);
        setFfmpegLoaded(true);
        console.log('FFmpeg loaded successfully');
      } catch (error) {
        console.error('Failed to load FFmpeg:', error);
      }
    };

    loadFFmpeg();
  }, []);

  // Modify stopRecording to convert audio locally before sending to server
  const stopRecording = async () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      mediaRecorderRef.current.stream.getTracks().forEach(track => track.stop());
      setIsRecording(false);
      setIsProcessingSpeech(true);

      // The rest will be handled in the onStop callback defined in startRecording
    }
  };

  // Update startRecording to handle conversion with ffmpeg.wasm
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          channelCount: 1,
          sampleRate: 16000
        }
      });

      // Check if browser is Safari (iOS or macOS)
      const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

      // Set appropriate MIME type based on browser
      const mimeType = isSafari ? 'audio/mp4' : 'audio/webm';

      const mediaRecorder = new MediaRecorder(stream, { mimeType });
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (e) => {
        audioChunksRef.current.push(e.data);
      };

      mediaRecorder.onstop = async (e) => {
        // Create blob with the appropriate MIME type
        const blob = new Blob(audioChunksRef.current, { type: mimeType });
        audioChunksRef.current = [];

        try {
          if (ffmpegLoaded && ffmpeg) {
            // Use different input filename based on format
            const inputFileName = isSafari ? 'recording.mp4' : 'recording.webm';
            const wavFileName = 'recording.wav';

            // Write the audio file to ffmpeg's virtual file system
            await ffmpeg.writeFile(inputFileName, await fetchFile(blob));

            // Run ffmpeg command to convert to wav (same for both formats)
            await ffmpeg.exec([
              '-i', inputFileName,
              '-acodec', 'pcm_s16le',
              '-ar', '16000',
              '-ac', '1',
              wavFileName
            ]);

            // Read the converted wav file
            const wavData = await ffmpeg.readFile(wavFileName);
            const wavBlob = new Blob([wavData.buffer], { type: 'audio/wav' });

            // Clean up the virtual file system
            await ffmpeg.deleteFile(inputFileName);
            await ffmpeg.deleteFile(wavFileName);

            // Convert to base64
            const reader = new FileReader();
            reader.readAsDataURL(wavBlob);
            reader.onloadend = async () => {
              const base64data = reader.result;

              // Get advisor name from localStorage
              const advisorName = localStorage.getItem('advisorName') || '';

              // Use API_URL constant for the endpoint
              const response = await fetch(`${API_URL}/api/speech-to-text`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  audio: base64data,
                  language: getSpeechLanguage(),
                  advisor_name: advisorName,
                  scenario_id: selectedScenario
                })
              });

              if (response.ok) {
                const data = await response.json();
                // Check the setting and either send directly or set input
                if (sendVoiceDirectly) {
                  handleSend(data.text); // Send directly
                } else {
                  setInput(data.text); // Set input field
                }
              } else {
                console.error('Speech recognition failed');
              }
              setIsProcessingSpeech(false);
            };
          } else {
            // Fallback to sending webm to the server if ffmpeg isn't loaded
            const reader = new FileReader();
            reader.readAsDataURL(blob);
            reader.onloadend = async () => {
              const base64data = reader.result;

              // Get advisor name from localStorage
              const advisorName = localStorage.getItem('advisorName') || '';

              // Also use API_URL in the fallback path
              const response = await fetch(`${API_URL}/api/speech-to-text`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  audio: base64data,
                  language: getSpeechLanguage(),
                  advisor_name: advisorName, // Make sure advisor_name is included here too
                  scenario_id: selectedScenario
                })
              });

              if (response.ok) {
                const data = await response.json();
                 // Check the setting and either send directly or set input
                if (sendVoiceDirectly) {
                  handleSend(data.text); // Send directly
                } else {
                  setInput(data.text); // Set input field
                }
              } else {
                console.error('Speech recognition failed');
              }
              setIsProcessingSpeech(false);
            };
          }
        } catch (error) {
          console.error('Error processing audio:', error);
          setIsProcessingSpeech(false);
        }
      };

      mediaRecorder.start();
      setIsRecording(true);
    } catch (error) {
      console.error('Error accessing microphone:', error);
      setIsProcessingSpeech(false); // Ensure processing state is reset on error
    }
  };

  // Refactor handleSend to accept optional text
  const handleSend = async (textToSend = null) => {
    const messageText = textToSend ?? input.trim(); // Use provided text or trimmed input
    if (!messageText) return; // Check if there's any text to send

    const nodeForThisTurn = activeNode; // Capture the active node *before* this turn
    const startTime = performance.now();
    const advisorName = localStorage.getItem('advisorName') || '';
    const advisorGender = localStorage.getItem('advisorGender') || 'male';
    const userMessage = {
      id: Date.now(),
      text: messageText, // Use the determined message text
      sender: 'user',
      timestamp: new Date().toISOString(),
      node: nodeForThisTurn // Associate previous node with user message
    };

    setMessages(prev => [...prev, userMessage]);
    // Only clear input if text wasn't sent directly from voice
    if (textToSend === null) {
       setInput('');
    }
    setIsLoading(true);

    try {
      // Get the current scenario object
      const currentScenario = scenarios.find(s => s.id === selectedScenario) || {};

      // Create sanitized history for the API call
      const serializableHistory = [...messages, userMessage].map(msg => ({
        role: msg.sender === 'user' ? 'user' : 'assistant',
        content: msg.text
        // Only include role and content
      }));

      const response = await axios.post(`${API_URL}/api/chat`, {
        question: messageText, // Send the correct message text
        model: selectedModel,
        scenario_id: selectedScenario,
        cartesia_id: currentScenario.cartesia_id || '',
        difficulty: difficulty,
        response_length: responseLength,
        advisor_name: advisorName,
        advisor_gender: advisorGender,
        current_stage: currentStage,
        language: getUiLanguage(),
        history: serializableHistory, // Use the sanitized history
        active_node: activeNode
      });
      const endTime = performance.now();
      const responseTime = (endTime - startTime).toFixed(2);
      const messageId = Date.now();

      console.log("Full API response:", response.data);

      // Extract the full response data - check all possible locations
      let fullResponse = {};
      if (response.data.response.full_response) {
        fullResponse = response.data.response.full_response;
      } else if (response.data.response._debug?.full_response) {
        fullResponse = response.data.response._debug.full_response;
      }

      console.log("Extracted fullResponse:", fullResponse);

      // Update current stage from the response if available
      if (fullResponse.current_stage) {
        console.log(`Updating current stage to: ${fullResponse.current_stage}`);
        setCurrentStage(fullResponse.current_stage);
      } else if (response.data.response.current_stage) {
        console.log(`Updating current stage to: ${response.data.response.current_stage}`);
        setCurrentStage(response.data.response.current_stage);
      }

      // Log next stage if provided
      if (fullResponse.next_stage) {
        console.log(`Next stage suggested: ${fullResponse.next_stage}`);
      }

      const botMessage = {
        id: messageId,
        text: response.data.response.response,
        sender: 'bot',
        timestamp: new Date().toISOString(),
        responseTime: responseTime,
        backendDuration: (response.data.response.duration * 1000).toFixed(2),
        currentStage: fullResponse.current_stage || response.data.response.current_stage,
        nextStage: fullResponse.next_stage || response.data.response.next_stage,
        node: nodeForThisTurn // Associate previous node with bot message (ensure only one line)
      };

      setMessages(prev => [...prev, botMessage]);
      setDebugInfo(botMessage.debug);

      // Check if chat is complete from response
      if (fullResponse.is_end_of_chat) {
        setIsChatComplete(true);
        setIsTimerActive(false); // Stop the timer when chat is complete
      }

      // Now call both API endpoints in parallel
      try {
        setIsLoading(false);
        setIsFlowchartLoading(true);

        // Only set loading state for advisor message if the feature is enabled
        if (showAdvisorMessage) {
          setIsLoadingAdvisorMessage(true);
        }

        // Get the prompt specifically for flowchart from the debug info
        const flowchartPrompt = response.data.response._debug?.prompt_for_flowchart ||
                                response.data.response._debug?.prompt || '';

        // Get the customer response from the API response
        const customerResponse = response.data.response.response || '';

        // Get the current stage
        const currentStageValue = fullResponse.current_stage || response.data.response.current_stage || currentStage;

        const combinedPrompt = `${flowchartPrompt}\n\n最後客戶的回應：${customerResponse}\n\nIMPORTANT: 「客戶」當前階段：${currentStageValue}`;

        // Sanitize history before sending to node completion status API
        const nodeStatusHistory = [...messages, userMessage, botMessage].map(msg => ({
           role: msg.sender === 'user' ? 'user' : 'assistant',
           content: msg.text
         }));

        // Call the new node completion status API
        const nodeStatusResponse = await axios.post(`${API_URL}/api/get_node_completion_status`, {
          scenario_id: selectedScenario,
          prompt: combinedPrompt,
          nodes: nodes || [], // Use previous nodes data or empty array if none
          history: nodeStatusHistory // Use sanitized history
        });

        if (nodeStatusResponse.data) {
          handleNodeStatusResponse(nodeStatusResponse.data);

          // After getting node status, call get-advisor-message if enabled
          if (showAdvisorMessage) {
            console.log("messages:::")
            console.log(messages)

            // Sanitize history before sending to advisor message API
            const advisorHistory = [...messages, userMessage, botMessage].map(msg => ({
              role: msg.sender === 'user' ? 'user' : 'assistant',
              content: msg.text
            }));

            const advisorResponse = await axios.post(`${API_URL}/api/get-advisor-message`, {
              history: advisorHistory, // Use sanitized history
              scenario_id: selectedScenario,
              advisor_name: advisorName,
              advisor_gender: advisorGender,
              current_stage: currentStageValue,
              active_node: nodeStatusResponse.data.active_node,
              language: getUiLanguage()
            });

            if (advisorResponse.data && advisorResponse.data.advisor_message) {
              setAdvisorMessage(advisorResponse.data.advisor_message);
              // Set flag to skip the next useEffect advisor fetch
              skipNextAdvisorFetch.current = true;
              setIsAdvisorMessageDisliked(false); // Correctly use the state setter
            }
          }
        }
      } catch (error) {
        console.error('Error in API calls:', error);
      } finally {
        setIsFlowchartLoading(false);
        if (showAdvisorMessage) {
          setIsLoadingAdvisorMessage(false);
          setIsAdvisorMessageDisliked(false); // Correctly use the state setter
        }
      }

      // Save conversation to localStorage
      const saveConversationToHistory = async () => {
        try {
          // Get existing history
          const existingHistory = localStorage.getItem('chatHistory');
          let chatHistory = [];

          if (existingHistory) {
            try {
              chatHistory = JSON.parse(existingHistory);
            } catch (error) {
              console.error('Error parsing chat history:', error);
              chatHistory = [];
            }
          }

          // Always create a new conversation entry instead of updating existing ones
          const scenarioTitle = scenarios.find(s => s.id === selectedScenario)?.title || 'Unknown Scenario';

          // Get member_id from localStorage using user object
          const userData = localStorage.getItem('user');
          console.log('Debug - Raw user data from localStorage:', userData); // Debug log
          let memberId = null;

          if (userData) {
            try {
              const user = JSON.parse(userData);
              memberId = user.id;
              console.log('Debug - Member ID from user object:', memberId); // Debug log
            } catch (error) {
              console.error('Error parsing user data:', error);
            }
          }

          // Create new conversation with timestamp, messages, and scenario info
          const newConversation = {
            id: currentConversationId, // Use state variable
            timestamp: new Date().toISOString(),
            scenarioId: selectedScenario,
            scenarioTitle: scenarioTitle,
            messages: messages.map(msg => ({
              sender: msg.sender,
              text: msg.text
            }))
          };

          console.log('Debug - New conversation object:', newConversation); // Debug log

          // Add new conversation to history
          chatHistory.unshift(newConversation); // Add to beginning of array

          // Save updated history
          localStorage.setItem('chatHistory', JSON.stringify(chatHistory));

          // Save to database if connected
          try {
            // Prepare the messages array for database storage
            const messagesForDb = [...messages, userMessage, botMessage].map(msg => ({
              id: msg.id,
              text: msg.text,
              sender: msg.sender,
              timestamp: msg.timestamp,
              ...(msg.node && { node: msg.node }) // Conditionally add node field
            }));

            console.log('Debug - Prepared messages for database:', messagesForDb); // Debug log

            await axios.put(
              `${API_URL}/api/storage/conversations/${currentConversationId}`,
              {
                member_id: memberId ? parseInt(memberId) : null,
                scenario_id: selectedScenario,
                scenario_title: scenarioTitle,
                timestamp: new Date().toISOString(),
                messages: messagesForDb
              }
            );
          } catch (err) {
            console.error('Error saving conversation to database:', err);
            console.error('Error details:', {
              message: err.message,
              stack: err.stack,
              code: err.code,
              detail: err.detail
            });
            // Don't throw here, just log the error
          }
        } catch (err) {
          console.error('Error in saveConversationToHistory:', err);
          // Don't throw here, just log the error
        }
      };

      // Call the function to save the conversation
      saveConversationToHistory();

    } catch (error) {
      console.error('Error:', error);
      const errorMessage = {
        id: Date.now(),
        text: tr('不好意思，我聽不清楚，請再說一次好嗎？', "Sorry, I couldn't hear that clearly. Could you say it again?"),
        sender: 'bot',
        timestamp: new Date().toISOString(),
        isError: true
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const toggleDebugInfo = () => {
    setIsDebugOpen(prev => !prev);
  };

  const handleCloseNameDialog = () => {
    setShowNameDialog(false);
  };

  const handleGoToSettings = () => {
    window.location.href = '/settings';
  };

  const handleViewAnalysis = async () => {
    try {
      setIsAnalyzing(true);

      // Save conversation to history first if not already saved
      if (!currentConversationId) {
        const scenarioTitle = scenarios.find(s => s.id === selectedScenario)?.title || 'Unknown Scenario';
        const newConversationId = Date.now();
        const newConversation = {
          id: newConversationId,
          timestamp: new Date().toISOString(),
          scenarioId: selectedScenario,
          scenarioTitle: scenarioTitle,
          messages: messages.map(msg => ({
            sender: msg.sender,
            text: msg.text,
            ...(msg.node && { node: msg.node }) // Conditionally add node field
          }))
        };

        // Get existing history
        const existingHistory = localStorage.getItem('chatHistory');
        let chatHistory = [];

        if (existingHistory) {
          try {
            chatHistory = JSON.parse(existingHistory);
          } catch (error) {
            console.error('Error parsing chat history:', error);
            chatHistory = [];
          }
        }

        // Add new conversation to history
        chatHistory.unshift(newConversation);

        // Save updated history
        localStorage.setItem('chatHistory', JSON.stringify(chatHistory));

        // Set the conversation ID
        setCurrentConversationId(newConversationId);
      }

      // Sanitize history for analysis API
      const analysisHistory = messages.map(msg => ({
        sender: msg.sender,
        text: msg.text,
        ...(msg.node && { node: msg.node }) // Keep node if present, it's serializable
        // Explicitly exclude other potentially complex fields
      }));

      const response = await axios.post(`${API_URL}/api/analysis`, {
        scenario_id: selectedScenario,
        history: analysisHistory, // Use sanitized history
        nodes: nodes,
        language: getUiLanguage(),
        conversation_id: currentConversationId
      });

      if (response.data) {
        // Parse the analysis string into JSON if it's not already
        let analysisData;
        try {
          analysisData = typeof response.data.analysis === 'string'
            ? JSON.parse(response.data.analysis)
            : response.data.analysis;
        } catch (e) {
          console.error('Error parsing analysis data:', e);
          analysisData = response.data.analysis;
        }

        setAnalysisData(analysisData);
        setAnalysisOpen(true);
      }
    } catch (error) {
      console.error('Error getting analysis:', error);
      // Handle error appropriately
    } finally {
      setIsAnalyzing(false);
    }
  };

  const handleRegenerateMessage = async (message) => {
    // Only allow regenerating bot messages
    if (message.sender !== 'bot') return;

    // Find the user message that preceded this bot message
    const messageIndex = messages.findIndex(m => m.id === message.id);
    if (messageIndex <= 0) return; // Can't regenerate if no preceding message

    const userMessage = messages[messageIndex - 1];
    if (userMessage.sender !== 'user') return; // Ensure it's a user message

    // Set loading state
    setIsLoading(true);
    setRegeneratingMessageId(message.id);

    // Create a new array of messages without the bot message we're regenerating
    const updatedMessages = messages.filter(m => m.id !== message.id);
    setMessages(updatedMessages);

    try {
      // Capture the node associated with the user message being replayed
      const nodeForRegen = userMessage.node || activeNode;
      const startTime = performance.now();
      const advisorName = localStorage.getItem('advisorName') || '';
      const advisorGender = localStorage.getItem('advisorGender') || 'male';

      // Get the current scenario object
      const currentScenario = scenarios.find(s => s.id === selectedScenario) || {};

      // Create sanitized history for the API call
      const serializableHistory = updatedMessages.map(msg => ({
        role: msg.sender === 'user' ? 'user' : 'assistant',
        content: msg.text
        // Only include role and content
      }));

      // Call the API with the same user message but without the bot message we're regenerating
      const response = await axios.post(`${API_URL}/api/chat`, {
        question: userMessage.text,
        model: selectedModel,
        scenario_id: selectedScenario,
        cartesia_id: currentScenario.cartesia_id || '',
        difficulty: difficulty,
        response_length: responseLength,
        advisor_name: advisorName,
        advisor_gender: advisorGender,
        current_stage: currentStage,
        language: getUiLanguage(),
        history: serializableHistory, // Use sanitized history
        active_node: nodeForRegen
      });
      const endTime = performance.now();
      const responseTime = (endTime - startTime).toFixed(2);
      const messageId = Date.now();

      console.log("Full API response:", response.data);

      // Extract the full response data - check all possible locations
      let fullResponse = {};
      if (response.data.response.full_response) {
        fullResponse = response.data.response.full_response;
      } else if (response.data.response._debug?.full_response) {
        fullResponse = response.data.response._debug.full_response;
      }

      console.log("Extracted fullResponse:", fullResponse);

      // Update current stage from the response if available
      if (fullResponse.current_stage) {
        console.log(`Updating current stage to: ${fullResponse.current_stage}`);
        setCurrentStage(fullResponse.current_stage);
      } else if (response.data.response.current_stage) {
        console.log(`Updating current stage to: ${response.data.response.current_stage}`);
        setCurrentStage(response.data.response.current_stage);
      }

      const botMessage = {
        id: messageId,
        text: response.data.response.response,
        sender: 'bot',
        timestamp: new Date().toISOString(),
        responseTime: responseTime,
        backendDuration: (response.data.response.duration * 1000).toFixed(2),
        currentStage: fullResponse.current_stage || response.data.response.current_stage,
        nextStage: fullResponse.next_stage || response.data.response.next_stage,
        node: nodeForRegen // Associate previous node with regenerated bot message
      };

      setMessages(prev => [...prev, botMessage]);
      setDebugInfo(botMessage.debug);

      // Call both API endpoints in parallel
      try {
        setIsFlowchartLoading(true);

        // Only set loading state for advisor message if the feature is enabled
        if (showAdvisorMessage) {
          setIsLoadingAdvisorMessage(true);
        }

        const flowchartPrompt = response.data.response._debug?.prompt_for_flowchart ||
                                response.data.response._debug?.prompt || '';

        const customerResponse = response.data.response.response || '';

        const currentStageValue = fullResponse.current_stage || response.data.response.current_stage || currentStage;

        const combinedPrompt = `${flowchartPrompt}\n\n客戶：${customerResponse}\n\nIMPORTANT: 「客戶」當前階段：${currentStageValue}`;

        // Format history for the advisor message API request
        const updatedHistoryWithNewBot = [...updatedMessages, botMessage];

        // Sanitize history before sending to advisor message API
        const advisorHistory = updatedHistoryWithNewBot.map(msg => ({
          role: msg.sender === 'user' ? 'user' : 'assistant',
          content: msg.text
        }));

        // Only execute the advisor message API call if the feature is enabled
        if (showAdvisorMessage) {
          // Execute both API calls in parallel
          const [flowchartResponse, advisorResponse] = await Promise.all([
            axios.post(`${API_URL}/api/generate_mermaid_with_history`, {
              scenario_id: selectedScenario,
              prompt: combinedPrompt
            }),

            axios.post(`${API_URL}/api/get-advisor-message`, {
              history: advisorHistory, // Use sanitized history
              scenario_id: selectedScenario,
              advisor_name: advisorName,
              advisor_gender: advisorGender,
              current_stage: currentStageValue,
              active_node: nodeForRegen,
              language: getUiLanguage()
            })
          ]);

          // Process flowchart response
          if (flowchartResponse.data) {
            handleNodeStatusResponse(flowchartResponse.data);
          }

          // Process advisor message response
          if (advisorResponse.data && advisorResponse.data.advisor_message) {
            setAdvisorMessage(advisorResponse.data.advisor_message);
            // Set flag to skip the next useEffect advisor fetch
            skipNextAdvisorFetch.current = true;
            setIsAdvisorMessageDisliked(false); // Correctly use the state setter
          }
        } else {
          // If advisor message is disabled, only call the flowchart API
          const flowchartResponse = await axios.post(`${API_URL}/api/generate_mermaid_with_history`, {
            scenario_id: selectedScenario,
            prompt: combinedPrompt
          });

          // Process flowchart response
          if (flowchartResponse.data) {
            handleNodeStatusResponse(flowchartResponse.data);
          }
        }
      } catch (error) {
        console.error('Error in parallel API calls:', error);
      } finally {
        setIsFlowchartLoading(false);
        if (showAdvisorMessage) {
          setIsLoadingAdvisorMessage(false);
          setIsAdvisorMessageDisliked(false); // Correctly use the state setter
        }
      }

    } catch (error) {
      console.error('Error regenerating message:', error);
      // Add the original message back if regeneration fails
      setMessages(prev => [...prev, message]);
    } finally {
      setIsLoading(false);
      setRegeneratingMessageId(null);
    }
  };

  const handleEditMessage = async (messageId, newText) => {
    // Capture the active node *before* the edit is applied
    const nodeForEditTurn = activeNode;

    // First update the edited message
    setMessages(prevMessages => {
      const messageIndex = prevMessages.findIndex(m => m.id === messageId);
      if (messageIndex === -1) return prevMessages;

      // Create new array with edited message and remove subsequent messages
      const newMessages = prevMessages.slice(0, messageIndex + 1);
      newMessages[messageIndex] = {
        ...newMessages[messageIndex],
        text: newText,
        isEdited: true,
        node: nodeForEditTurn // Associate previous node with edited user message
      };

      return newMessages;
    });
    setEditingMessageId(null);

    // Then trigger a new API call
    const startTime = performance.now();
    setIsLoading(true);
    try {
      const advisorName = localStorage.getItem('advisorName') || '';
      const advisorGender = localStorage.getItem('advisorGender') || 'male';
      const currentScenario = scenarios.find(s => s.id === selectedScenario) || {};

      // Get messages up to and including the edited message
      const messageIndex = messages.findIndex(m => m.id === messageId);
      const relevantMessages = messages.slice(0, messageIndex + 1).map(msg => ({
        role: msg.sender === 'user' ? 'user' : 'assistant',
        content: msg.id === messageId ? newText : msg.text
      }));

      // Sanitize history for the API call
      const serializableHistory = relevantMessages.map(msg => ({
        role: msg.sender === 'user' ? 'user' : 'assistant',
        content: msg.text
      }));

      const response = await axios.post(`${API_URL}/api/chat`, {
        question: newText,
        model: selectedModel,
        scenario_id: selectedScenario,
        cartesia_id: currentScenario.cartesia_id || '',
        difficulty: difficulty,
        response_length: responseLength,
        advisor_name: advisorName,
        advisor_gender: advisorGender,
        current_stage: currentStage,
        language: getUiLanguage(),
        history: serializableHistory, // Use sanitized history
        active_node: activeNode
      });

      const endTime = performance.now();
      const responseTime = (endTime - startTime).toFixed(2);
      const newMessageId = Date.now(); // Changed variable name to avoid conflict

      // Add the same console logging as in handleSend
      console.log("Full API response:", response.data);

      // Extract the full response data - check all possible locations
      let fullResponse = {};
      if (response.data.response.full_response) {
        fullResponse = response.data.response.full_response;
      } else if (response.data.response._debug?.full_response) {
        fullResponse = response.data.response._debug.full_response;
      }

      console.log("Extracted fullResponse:", fullResponse);

      // Update current stage from the response if available
      if (fullResponse.current_stage) {
        console.log(`Updating current stage to: ${fullResponse.current_stage}`);
        setCurrentStage(fullResponse.current_stage);
      } else if (response.data.response.current_stage) {
        console.log(`Updating current stage to: ${response.data.response.current_stage}`);
        setCurrentStage(response.data.response.current_stage);
      }

      // Log next stage if provided
      if (fullResponse.next_stage) {
        console.log(`Next stage suggested: ${fullResponse.next_stage}`);
      }

      const botMessage = {
        id: newMessageId,
        text: response.data.response.response,
        sender: 'bot',
        timestamp: new Date().toISOString(),
        responseTime: responseTime,
        backendDuration: (response.data.response.duration * 1000).toFixed(2),
        currentStage: fullResponse.current_stage || response.data.response.current_stage,
        nextStage: fullResponse.next_stage || response.data.response.next_stage,
        node: nodeForEditTurn // Associate previous node with the new bot message
      };

      // Add the new bot response
      setMessages(prevMessages => {
        const msgIndex = prevMessages.findIndex(m => m.id === messageId);
        return [...prevMessages.slice(0, msgIndex + 1), botMessage];
      });

      // Update debug info
      setDebugInfo(botMessage.debug);

      // Check if chat is complete from response
      if (fullResponse.is_end_of_chat) {
        setIsChatComplete(true);
        setIsTimerActive(false); // Stop the timer when chat is complete
      }

      // Update flowchart if needed
      try {
        setIsFlowchartLoading(true);
        const flowchartPrompt = response.data.response._debug?.prompt_for_flowchart ||
                              response.data.response._debug?.prompt || '';

        const customerResponse = response.data.response.response || '';
        // Use the updated stage value from the response
        const stageValue = fullResponse.current_stage || response.data.response.current_stage || currentStage;

        const combinedPrompt = `${flowchartPrompt}\n\n客戶：${customerResponse}\n\nIMPORTANT: 「客戶」當前階段：${stageValue}`;

        const flowchartResponse = await axios.post(`${API_URL}/api/generate_mermaid_with_history`, {
          scenario_id: selectedScenario,
          prompt: combinedPrompt
        });

        // Call handleNodeStatusResponse with the flowchart data
        if (flowchartResponse.data) {
          handleNodeStatusResponse(flowchartResponse.data);
        }
      } catch (flowchartError) {
        console.error('Error generating flowchart:', flowchartError);
      } finally {
        setIsFlowchartLoading(false);
      }

    } catch (error) {
      console.error('Error:', error);
      const errorMessage = {
        id: Date.now(),
        text: tr('不好意思，我聽不清楚，請再說一次好嗎？', "Sorry, I couldn't hear that clearly. Could you say it again?"),
        sender: 'bot',
        timestamp: new Date().toISOString(),
        isError: true
      };
      setMessages(prevMessages => {
        const msgIndex = prevMessages.findIndex(m => m.id === messageId);
        return [...prevMessages.slice(0, msgIndex + 1), errorMessage];
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Add this useEffect to update timeRemaining when currentStage changes
  useEffect(() => {
    // Get the scenario data from wherever it's stored in your component
    const currentScenario = scenarioData; // Replace with your actual variable name

    if (currentScenario && currentScenario.stage && currentStage) {
      // Find the current stage object
      const stageObj = currentScenario.stage.find(s => s.name === currentStage);
      if (stageObj && stageObj.duration) {
        // Set timeLimit to the duration of the current stage in minutes, converted to seconds
        setTimeLimit(stageObj.duration * 60);
      } else {
        // Fallback to default duration if stage duration is not found
        setTimeLimit(3 * 60); // Default 3 minutes
      }
      // Reset stopwatch and time limit status when stage changes
      setElapsedTime(0);
      setIsTimeLimitReached(false);
    }
  }, [currentStage, scenarioData]); // Replace scenario with your actual variable name

  // Add this function to find the parent stage of a node
  const findParentStage = (nodes, activeNodeId) => {
    for (const node of nodes) {
      if (node.type === 'subgraph' && node.nodes) {
        const foundNode = node.nodes.find(n => n.id === activeNodeId);
        if (foundNode) {
          return node.text; // Return the stage name (e.g., "破冰階段")
        }
      }
    }
    return null; // Return null if no parent stage is found
  };

  // Modify the advisor message useEffect
  useEffect(() => {
    const fetchInitialAdvisorMessage = async () => {
      if (!showAdvisorMessage || !selectedScenario) return;

      // Skip if this is triggered by initial scenario load
      if (isInitialLoad.current) {
        isInitialLoad.current = false;
        return;
      }

      // Skip this fetch if we just got an advisor message in handleSend
      if (skipNextAdvisorFetch.current) {
        skipNextAdvisorFetch.current = false;
        return;
      }

      setIsLoadingAdvisorMessage(true);
      try {
        const advisorName = localStorage.getItem('advisorName') || '';
        const advisorGender = localStorage.getItem('advisorGender') || 'male';

        // Sanitize history before sending
        const advisorHistory = messages.map(msg => ({
          role: msg.sender === 'user' ? 'user' : 'assistant',
          content: msg.text
        }));

        const response = await axios.post(`${API_URL}/api/get-advisor-message`, {
          history: advisorHistory, // Use sanitized history
          scenario_id: selectedScenario,
          advisor_name: advisorName,
          advisor_gender: advisorGender,
          current_stage: currentStage,
          active_node: activeNode,
          language: getUiLanguage()
        });

        if (response.data && response.data.advisor_message) {
          setAdvisorMessage(response.data.advisor_message);
        }
      } catch (error) {
        console.error('Error fetching initial advisor message:', error);
      } finally {
        setIsLoadingAdvisorMessage(false);
        setIsAdvisorMessageDisliked(false); // Correctly use the state setter
      }
    };

    fetchInitialAdvisorMessage();
  }, [selectedScenario, showAdvisorMessage, currentStage, activeNode]);

  // Add this function after handleNodeStatusResponse
  const checkAllNodesCompleted = (nodes) => {
    if (!nodes || nodes.length === 0) return false;

    const checkNode = (node) => {
      if (node.type === 'subgraph' && node.nodes) {
        return node.nodes.every(checkNode);
      }
      return node.status === 'completed';
    };

    return nodes.every(checkNode);
  };

  // Modify handleNodeStatusResponse to check completion and update active node
  const handleNodeStatusResponse = (response) => {
    console.log("Processing node status response:", response);

    if (response && response.nodes) {
      setNodes(response.nodes);

      // Update active node if it exists in the response
      if (response.active_node) {
        setActiveNode(response.active_node);
        console.log("Updated active node to:", response.active_node);
      }

      // Check if all nodes are completed
      if (checkAllNodesCompleted(response.nodes)) {
        handleAutoAnalysis();
      }
    }
  };

  // Modify handleAutoAnalysis to include countdown
  const handleAutoAnalysis = async () => {
    if (isAnalysisLoading || analysisCountdown !== null) return;

    // Start countdown from 5
    setAnalysisCountdown(1);

    // Create countdown timer
    const countdownInterval = setInterval(() => {
      setAnalysisCountdown(prev => {
        if (prev <= 1) {
          clearInterval(countdownInterval);
          // handleViewAnalysis();
          setIsChatComplete(true);
          setIsTimerActive(false); // Stop the timer when chat is complete
          return null;
        }
        return prev - 1;
      });
    }, 1000);
  };

  // Modify the advisor message refresh function
  const handleRefreshAdvisorMessage = async () => {
    if (!showAdvisorMessage || isLoadingAdvisorMessage) return;
    setIsLoadingAdvisorMessage(true);

    try {
      const advisorName = localStorage.getItem('advisorName') || '';
      const advisorGender = localStorage.getItem('advisorGender') || 'male';

      // Sanitize history before sending
      const advisorHistory = messages.map(msg => ({
        role: msg.sender === 'user' ? 'user' : 'assistant',
        content: msg.text
      }));

      // Include current checkpoint status in the request
      const response = await axios.post(`${API_URL}/api/get-advisor-message`, {
        history: advisorHistory, // Use sanitized history
        scenario_id: selectedScenario,
        advisor_name: advisorName,
        advisor_gender: advisorGender,
        current_stage: currentStage,
        active_node: activeNode,
        current_checkpoint_status: checkpointStatus, // Send current status
        language: getUiLanguage()
      });

      if (response.data) {
        if (response.data.advisor_message) {
          setAdvisorMessage(response.data.advisor_message);
          // Set flag to skip the next useEffect advisor fetch
          skipNextAdvisorFetch.current = true;
        }

        // If response includes node status, merge it with current status
        if (response.data.checkpoint_status) {
          setCheckpointStatus(response.data.checkpoint_status);
        }
      }
    } catch (error) {
      console.error('Error refreshing advisor message:', error);
    } finally {
      setIsLoadingAdvisorMessage(false);
      setIsAdvisorMessageDisliked(false); // Reset dislike state after refresh
    }
  };

  // Verify the API/database path once when the chat mounts.
  useEffect(() => {
    axios.get(`${API_URL}/api/storage/health`)
      .then(() => setDbConnectionStatus('Connected to VM database'))
      .catch((error) => setDbConnectionStatus(`Database connection failed: ${error.message}`));
  }, []);

  // Add this useEffect to load initial flowchart
  useEffect(() => {
    const loadInitialNodeStatus = async () => {
      if (!selectedScenario) return;

      try {
        setIsFlowchartLoading(true);
        console.log("Loading initial node status for scenario:", selectedScenario);

        const nodeStatusResponse = await axios.post(`${API_URL}/api/get_node_completion_status`, {
          scenario_id: selectedScenario,
          prompt: '',  // Empty prompt for initial load
          nodes: [],  // Empty nodes for initial load
          history: [] // Empty history for initial load
        });

        console.log("Initial node status response:", nodeStatusResponse.data);
        console.log("Initial node status data:", nodeStatusResponse.data.nodes);

        if (nodeStatusResponse.data?.nodes) {
          handleNodeStatusResponse(nodeStatusResponse.data);
        }
      } catch (error) {
        console.error('Error loading initial node status:', error);
      } finally {
        setIsFlowchartLoading(false);
      }
    };

    loadInitialNodeStatus();
  }, [selectedScenario]); // Only depend on selectedScenario, not messages

  // Store message/advisor ratings through the VM API.
  const sendFeedback = async (sourceType, content, messageId = null) => {
    if (!currentConversationId) {
      console.warn('Cannot send feedback without a conversation ID');
      return;
    }
    const memberData = localStorage.getItem('user');
    const memberId = memberData ? parseInt(JSON.parse(memberData).id) : null;
    const advisorName = localStorage.getItem('advisorName') || '';
    const timestamp = new Date().toISOString();

    try {
      await axios.post(`${API_URL}/api/storage/content-ratings`, {
        member_id: memberId,
        conversation_id: currentConversationId,
        message_id: messageId,
        feedback_type: 'dislike',
        source_type: sourceType,
        content,
        scenario_id: selectedScenario,
        advisor_name: advisorName,
        timestamp
      });
    } catch (error) {
      console.error(`Error sending ${sourceType} dislike feedback directly to DB:`, error);
      console.error('Error details:', {
        message: error.message,
        stack: error.stack,
        code: error.code,
        detail: error.detail
      });
    }
  };

  return (
    <Container maxWidth="lg">
      <Box sx={{
        py: { xs: 2, md: 4 },
        height: '100vh',
        display: 'flex',
        flexDirection: 'column'
      }}
      ref={chatContainerRef}
      className="chat-container-main"
      >
        {/* Options Toggle Button */}
        <Tooltip title={isOptionsOpen ? tr('隱藏選項', 'Hide options') : tr('顯示選項', 'Show options')}>
          <IconButton
            onClick={() => setIsOptionsOpen(!isOptionsOpen)}
            sx={{
              m: 0,
              p: 0,
              display: { md: 'none' },
              bgcolor: 'transparent',
              '&:hover': {
                bgcolor: 'transparent'
              }
            }}
          >
            <MenuIcon />
          </IconButton>
        </Tooltip>

        {/* Scenario Toggle Button (now using icon only) */}
        <Tooltip title={isScenarioOpen ? tr('隱藏場景資訊', 'Hide scenario details') : tr('顯示場景資訊', 'Show scenario details')}>
          <IconButton
            onClick={() => setIsScenarioOpen(!isScenarioOpen)}
            sx={{
              m: 0,
              p: 0,
              display: { xs: 'block', md: 'block' },
              bgcolor: 'transparent',
              '&:hover': {
                bgcolor: 'transparent'
              }
            }}
          >
            {isScenarioOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </Tooltip>

        {/* Collapsible Options Section */}
        <Collapse
          in={isOptionsOpen}
          sx={{
            position: { xs: 'relative', md: 'static' },
            zIndex: 1000,
            bgcolor: 'background.default'
          }}
        >
          <Box sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            gap: 2
          }}>
            {/* Other Options */}
            <Box sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              gap: 2
            }}>
              <Box sx={{
                width: { xs: '100%', md: 200 },
                bgcolor: '#fff',
                borderRadius: 4,
                p: 2,
                boxShadow: '0 0 20px rgba(0,0,0,0.05)'
              }}>
                <DifficultySelector
                  difficulty={difficulty}
                  onDifficultyChange={handleDifficultyChange}
                />
              </Box>

              <Box sx={{
                width: { xs: '100%', md: 200 },
                bgcolor: '#fff',
                borderRadius: 4,
                p: 2,
                boxShadow: '0 0 20px rgba(0,0,0,0.05)'
              }}>
                <ResponseLengthSelector
                  responseLength={responseLength}
                  setResponseLength={setResponseLength}
                />
              </Box>

              <Box sx={{
                width: { xs: '100%', md: 200 },
                bgcolor: '#fff',
                borderRadius: 4,
                p: 2,
                boxShadow: '0 0 20px rgba(0,0,0,0.05)'
              }}>
                <ModelSelector
                  models={models}
                  selectedModel={selectedModel}
                  onModelChange={handleModelChange}
                />
              </Box>

              <FormControl fullWidth sx={{ mt: 2 }}>
                <InputLabel id="stage-select-label">{tr('當前階段', 'Current Stage')}</InputLabel>
                <Select
                  labelId="stage-select-label"
                  id="stage-select"
                  value={currentStage}
                  label={tr('當前階段', 'Current Stage')}
                  onChange={handleStageChange}
                >
                  {scenarios.find(s => s.id === selectedScenario)?.stage?.map((stageItem) => (
                    <MenuItem key={stageItem.name} value={stageItem.name}>
                      {translateContent(stageItem.name)}
                    </MenuItem>
                  )) || (
                    <MenuItem value="破冰階段">{translateContent('破冰階段')}</MenuItem>
                  )}
                </Select>
              </FormControl>
            </Box>
          </Box>
        </Collapse>

        {/* Main content area with chat and flowchart side by side */}
        <Box sx={{
          flex: 1,
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          gap: 2,
          overflow: 'hidden'
        }}>
          {/* Left Section - TopSection and Chat */}
          <Box sx={{
            flex: { xs: 1, md: 2 },
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            height: '100%'
          }}>
            {/* Top Section with Collapse */}
            <Collapse in={isScenarioOpen}>
              <Box sx={{
                bgcolor: '#fff',
                borderRadius: 4,
                boxShadow: '0 0 20px rgba(0,0,0,0.05)',
                mb: 2
              }}>
                <TopSection
                  selectedScenario={selectedScenario}
                  scenarios={scenarios}
                  translateContent={translateContent}
                  isLocalizationLoading={isLocalizationLoading}
                  sx={{
                    transition: 'margin-top 0.3s ease',
                    display: { xs: 'block', md: 'block' }
                  }}
                />

                {/* Add database connection status */}
                {dbConnectionStatus && (
                  <Box sx={{ mb: 1, display: 'flex', justifyContent: 'center' }}>
                    <Typography
                      variant="caption"
                      color={dbConnectionStatus.includes('failed') ? 'error' : 'success.main'}
                    >
                      {dbConnectionStatus}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Collapse>

            {/* Chat Section */}
            <Box sx={{
              flex: 1,
              bgcolor: '#fff',
              borderRadius: 4,
              boxShadow: '0 0 20px rgba(0,0,0,0.05)',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden'
            }}>
              <ChatMessages
                messages={messages}
                isLoading={isLoading}
                selectedScenario={selectedScenario}
                onRegenerateMessage={handleRegenerateMessage}
                editingMessageId={editingMessageId}
                setEditingMessageId={setEditingMessageId}
                onEditMessage={handleEditMessage}
                conversationId={currentConversationId}
                sendFeedback={sendFeedback} // Pass sendFeedback function
              />

              {/* Only show input section if chat is not complete */}
              {!isChatComplete && (
                <Box sx={{
                  p: { xs: 1, md: 2 },
                  borderTop: '1px solid',
                  borderColor: 'divider',
                  bgcolor: '#fff'
                }}>
                  <Box sx={{
                    display: 'flex',
                    gap: { xs: 1, md: 2 },
                    alignItems: 'center'
                  }}>
                    <Box sx={{
                      flex: 1,
                      bgcolor: '#f8fafc',
                      borderRadius: 3,
                      overflow: 'hidden',
                      position: 'relative'
                    }}>
                      <ChatInput
                        input={input}
                        setInput={setInput}
                        handleSend={handleSend}
                        isLoading={isLoading}
                      />
                      {isProcessingSpeech && (
                        <Box sx={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          backgroundColor: 'rgba(255, 255, 255, 0.8)',
                          zIndex: 1
                        }}>
                          <CircularProgress size={24} />
                        </Box>
                      )}
                    </Box>
                    <Box sx={{ display: 'flex', gap: { xs: 0.5, md: 1 } }}>
                      <IconButton
                        onClick={() => handleSend()}
                        disabled={!input.trim() || isLoading}
                        color="primary"
                        size={isMobile ? 'small' : 'medium'}
                        sx={{
                          bgcolor: theme => theme.palette.primary.main,
                          color: '#fff',
                          '&:hover': {
                            bgcolor: theme => theme.palette.primary.dark,
                          },
                          '&.Mui-disabled': {
                            bgcolor: 'action.disabledBackground',
                            color: 'action.disabled'
                          }
                        }}
                      >
                        <SendIcon />
                      </IconButton>
                      <Tooltip title={isRecording ? 'Stop Recording' : 'Start Voice Input'}>
                        <IconButton
                          onClick={isRecording ? stopRecording : startRecording}
                          color={isRecording ? 'error' : 'primary'}
                          size={isMobile ? 'small' : 'medium'}
                          sx={{
                            bgcolor: theme => isRecording ? theme.palette.error.main : theme.palette.primary.main,
                            color: '#fff',
                            '&:hover': {
                              bgcolor: theme => isRecording ? theme.palette.error.dark : theme.palette.primary.dark,
                            },
                            mr: 0.5 // Add some margin before the switch
                          }}
                        >
                          {isRecording ? <StopIcon /> : <MicIcon />}
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={tr('語音輸入處理後立即傳送', 'Send automatically after voice input is processed')}>
                        <Switch
                          checked={sendVoiceDirectly}
                          onChange={(e) => setSendVoiceDirectly(e.target.checked)}
                          size="small"
                          sx={{ alignSelf: 'center' }} // Center switch vertically
                        />
                      </Tooltip>
                    </Box>
                  </Box>
                </Box>
              )}
            </Box>
          </Box>

          {/* Right Section - Flowchart and Advisor Message */}
          {(nodes.length > 0 || isFlowchartLoading) && (
            <Box sx={{
              flex: { xs: 1, md: 1 },
              display: { xs: 'none', md: 'flex' },
              bgcolor: '#fff',
              borderRadius: 4,
              p: { xs: 1, md: 2 },
              boxShadow: '0 0 20px rgba(0,0,0,0.05)',
              overflow: 'auto',
              height: '100%',
              flexDirection: 'column'
            }}>
              {/* Stage and Timer Box */}
              <Box
                key={`stage-box-${currentStage}`}
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 2,
                  p: 1.5,
                  bgcolor: 'rgba(25, 118, 210, 0.1)',
                  borderRadius: 2,
                  color: 'primary.main',
                  transition: 'all 0.5s ease',
                  animation: `${highlightAnimation} 2s ease`,
                  boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 'bold',
                    animation: `${highlightAnimation} 2s ease`,
                  }}
                  key={`stage-text-${currentStage}`}
                >
                  {tr('當前階段', 'Current Stage')}: {translateContent(currentStage)}
                </Typography>

                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  p: 0.75,
                  bgcolor: isTimeLimitReached ? 'error.light' : elapsedTime > (timeLimit - 60) ? 'warning.light' : 'info.light',
                  borderRadius: 1.5,
                  transition: 'background-color 0.5s ease',
                }}>
                  <TimerIcon sx={{
                    mr: 1,
                    fontSize: '1.2rem',
                    color: isTimeLimitReached ? 'error.dark' : elapsedTime > (timeLimit - 60) ? 'warning.dark' : 'info.dark'
                  }} />
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 'bold',
                      color: isTimeLimitReached ? 'error.dark' : elapsedTime > (timeLimit - 60) ? 'warning.dark' : 'info.dark',
                      fontFamily: 'monospace',
                      lineHeight: 1
                    }}
                  >
                    {formatTime(elapsedTime)}
                  </Typography>
                </Box>
              </Box>

              {/* Flowchart */}
              <Box sx={{ flex: 1, overflow: 'auto' }}>
                <FlowchartView
                  nodes={nodes}
                  isLoading={isFlowchartLoading}
                  isLocalizationLoading={isLocalizationLoading}
                  translateContent={translateContent}
                  scenarioData={scenarioData}
                  checkpointStatus={checkpointStatus}
                />
              </Box>

              {/* Advisor Message Section - Always shown, content conditional */}
              <Box sx={{
                mt: 2,
                p: 2,
                borderTop: '1px solid',
                borderColor: 'divider',
                bgcolor: 'rgba(25, 118, 210, 0.05)',
                borderRadius: 2
              }}>
                <Box sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 1
                }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <LightbulbIcon sx={{ fontSize: '1.2rem' }} />
                      {tr('建議提示', 'Advisor Hint')}
                      <IconButton
                        size="small"
                        onClick={handleRefreshAdvisorMessage}
                        disabled={!showAdvisorMessage || isLoadingAdvisorMessage}
                        sx={{
                          color: 'primary.main',
                          '&.Mui-disabled': {
                            color: 'action.disabled'
                          }
                        }}
                      >
                        <RefreshIcon sx={{ fontSize: '1rem' }} />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => {
                          navigator.clipboard.writeText(advisorMessage)
                            .then(() => {
                              console.log('Copied to clipboard');
                            })
                            .catch(err => console.error('Failed to copy:', err));
                        }}
                        disabled={!showAdvisorMessage || !advisorMessage}
                        sx={{
                          color: 'primary.main',
                          '&.Mui-disabled': {
                            color: 'action.disabled'
                          }
                        }}
                      >
                        <ContentCopyIcon sx={{ fontSize: '1rem' }} />
                      </IconButton>
                    </Box>
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Tooltip title={isAdvisorMessageDisliked ? tr('已倒讚', 'Disliked') : tr('倒讚', 'Dislike')}>
                      <IconButton
                        size="small"
                        onClick={() => {
                          const nextState = !isAdvisorMessageDisliked;
                          setIsAdvisorMessageDisliked(nextState);
                          if (nextState) { // Only send feedback when disliking
                            sendFeedback('advisor', advisorMessage);
                          }
                        }}
                        disabled={!showAdvisorMessage || !advisorMessage}
                        sx={{
                          color: 'primary.main',
                          '&.Mui-disabled': {
                            color: 'action.disabled'
                          }
                        }}
                      >
                        {isAdvisorMessageDisliked ? <ThumbDownAltIcon sx={{ fontSize: '1rem' }} /> : <ThumbDownAltOutlinedIcon sx={{ fontSize: '1rem' }} />}
                      </IconButton>
                    </Tooltip>
                    <Switch
                      size="small"
                      checked={showAdvisorMessage}
                      onChange={(e) => setShowAdvisorMessage(e.target.checked)}
                    />
                  </Box>
                </Box>

                {showAdvisorMessage && (
                  isLoadingAdvisorMessage ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                      <CircularProgress size={24} />
                    </Box>
                  ) : (
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                      {advisorMessage}
                    </Typography>
                  )
                )}
              </Box>
            </Box>
          )}
        </Box>

        {/* Mobile Flowchart and Advisor Message Section - Only visible on mobile */}
        {(nodes.length > 0 || isFlowchartLoading) && (
          <Box sx={{
            mt: 2,
            p: { xs: 1, md: 2 },
            bgcolor: '#fff',
            borderRadius: 4,
            boxShadow: '0 0 20px rgba(0,0,0,0.05)',
            display: { xs: 'block', md: 'none' }
          }}>
            <FlowchartView
              nodes={nodes}
              isLoading={isFlowchartLoading}
              isLocalizationLoading={isLocalizationLoading}
              translateContent={translateContent}
              scenarioData={scenarioData}
              checkpointStatus={checkpointStatus}
            />

            {/* Mobile Advisor Message - Always shown, content conditional */}
            <Box sx={{
              mt: 2,
              p: 2,
              borderTop: '1px solid',
              borderColor: 'divider',
              bgcolor: 'rgba(25, 118, 210, 0.05)',
              borderRadius: 2
            }}>
              <Box sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 1
              }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <LightbulbIcon sx={{ fontSize: '1.2rem' }} />
                    {tr('建議提示', 'Advisor Hint')}
                    <IconButton
                      size="small"
                      onClick={handleRefreshAdvisorMessage}
                      disabled={!showAdvisorMessage || isLoadingAdvisorMessage}
                      sx={{
                        color: 'primary.main',
                        '&.Mui-disabled': {
                          color: 'action.disabled'
                        }
                      }}
                    >
                      <RefreshIcon sx={{ fontSize: '1rem' }} />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => {
                        navigator.clipboard.writeText(advisorMessage)
                          .then(() => {
                            console.log('Copied to clipboard');
                          })
                          .catch(err => console.error('Failed to copy:', err));
                      }}
                      disabled={!showAdvisorMessage || !advisorMessage}
                      sx={{
                        color: 'primary.main',
                        '&.Mui-disabled': {
                          color: 'action.disabled'
                        }
                      }}
                    >
                      <ContentCopyIcon sx={{ fontSize: '1rem' }} />
                    </IconButton>
                    <Tooltip title={isAdvisorMessageDisliked ? tr('已倒讚', 'Disliked') : tr('倒讚', 'Dislike')}>
                      <IconButton
                        size="small"
                        onClick={() => {
                          const nextState = !isAdvisorMessageDisliked;
                          setIsAdvisorMessageDisliked(nextState);
                          if (nextState) { // Only send feedback when disliking
                            sendFeedback('advisor', advisorMessage);
                          }
                        }}
                        disabled={!showAdvisorMessage || !advisorMessage}
                        sx={{
                          color: 'primary.main',
                          '&.Mui-disabled': {
                            color: 'action.disabled'
                          }
                        }}
                      >
                        {isAdvisorMessageDisliked ? <ThumbDownAltIcon sx={{ fontSize: '1rem' }} /> : <ThumbDownAltOutlinedIcon sx={{ fontSize: '1rem' }} />}
                      </IconButton>
                    </Tooltip>
                    <Switch
                      size="small"
                      checked={showAdvisorMessage}
                      onChange={(e) => setShowAdvisorMessage(e.target.checked)}
                    />
                  </Box>
                </Typography>
              </Box>

              {showAdvisorMessage && (
                isLoadingAdvisorMessage ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                    <CircularProgress size={24} />
                  </Box>
                ) : (
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                    {advisorMessage}
                  </Typography>
                )
              )}
            </Box>
          </Box>
        )}

        {/* Debug Section */}
        {showDebug && debugInfo && (
          <Box sx={{
            mt: 2,
            p: { xs: 1, md: 2 },
            bgcolor: '#f5f5f5',
            borderRadius: 4,
            fontSize: { xs: '0.875rem', md: '1rem' }
          }}>
            <Button
              onClick={toggleDebugInfo}
              startIcon={isDebugOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              size={isMobile ? 'small' : 'medium'}
            >
              Debug Information
            </Button>
            <Collapse in={isDebugOpen} timeout="auto" unmountOnExit>
              <pre style={{
                overflowX: 'auto',
                whiteSpace: 'pre-wrap',
                wordWrap: 'break-word'
              }}>
                {JSON.stringify(debugInfo, null, 2).replace(/\\n/g, '\n')}
              </pre>
            </Collapse>
          </Box>
        )}

        {/* Show completion message when chat is complete */}
        {isChatComplete && (
          <Box sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: 1,
            mt: 2
          }}>
            <img
              src="/images/rocket.svg"
              alt={tr('完成', 'Complete')}
              style={{ width: 28, height: 28 }}
            />
            <Typography
              variant="body1"
              sx={{
                color: '#1976d2',
                fontWeight: 500
              }}
            >
              {isAnalyzing
                ? tr('正在分析對話內容...', 'Analyzing the conversation...')
                : analysisCountdown
                  ? tr(`分析將在 ${analysisCountdown} 秒後開始...`, `Analysis starts in ${analysisCountdown} seconds...`)
                  : tr('你已完成練習！', 'You completed the practice!')
              }
            </Typography>
            <Button
              variant="contained"
              color="primary"
              size="small"
              sx={{ ml: 2 }}
              onClick={handleViewAnalysis}
              disabled={isAnalyzing || isAnalysisLoading || analysisCountdown !== null}
            >
              {isAnalyzing || isAnalysisLoading ? (
                <CircularProgress size={20} sx={{ color: 'white' }} />
              ) : tr('查看分析', 'View Analysis')}
            </Button>
          </Box>
        )}

        {/* Add test button for handleViewAnalysis */}
        <Box
          sx={{
            position: 'fixed',
            bottom: 10,
            right: 10,
            zIndex: 9999
          }}
        >
          <Tooltip title="Test Analysis (Dev Only)">
            <IconButton
              size="small"
              onClick={handleViewAnalysis}
              sx={{
                width: 24,
                height: 24,
                bgcolor: 'rgba(25, 118, 210, 0.1)',
                '&:hover': {
                  bgcolor: 'rgba(25, 118, 210, 0.2)'
                },
                fontSize: '0.6rem',
                color: 'text.secondary'
              }}
            >
              T
            </IconButton>
          </Tooltip>
        </Box>

        {/* Add FeedbackComponent at the end */}
        <FeedbackComponent targetRef={chatContainerRef} conversationId={currentConversationId} />
      </Box>

      {/* Add this Dialog component before the closing Container tag */}
      <Dialog
        open={showNameDialog}
        onClose={handleCloseNameDialog}
        aria-labelledby="name-dialog-title"
        aria-describedby="name-dialog-description"
      >
        <DialogTitle id="name-dialog-title">
          {tr('請設定名字', 'Set Your Name')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="name-dialog-description">
            {tr('為了提供更好的服務體驗，請先至設定頁面輸入名字。', 'Please enter your name in Settings before starting so the experience can be personalized.')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseNameDialog}>{tr('稍後設定', 'Later')}</Button>
          <Button onClick={handleGoToSettings} variant="contained" autoFocus>
            {tr('前往設定', 'Go to Settings')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add the Analysis Dialog */}
      <AnalysisDialog
        open={analysisOpen}
        onClose={() => setAnalysisOpen(false)}
        data={analysisData}
        nodes={nodes}
        translateContent={translateContent}
        practice_time={elapsedTime} // Pass full seconds instead of just minutes
        conversationId={currentConversationId}
      />
    </Container>
  );
};

export default ChatBox;
